import type { Profile } from 'api/connector/types'

import { connectorAPI } from 'actions/connector.js'
import clsx from 'clsx'
import { AuthTemplate } from 'components/templates/authTemplate'
import { confirmLinkedInProfile } from 'features/linkedin-login/api'
import type { LinkedInLoginHeaders } from 'features/linkedin-login/types'
import isEmpty from 'lodash/isEmpty'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { appConnector } from 'shared/connector'
import { notifyError } from 'shared/errors/notificate.ts'
import { typo } from 'shared/styles'
import { Button, LoadingButton, StackIcon } from 'shared/ui'
import { ControlledDialog } from 'shared/ui/Dialog'

import css from './NoLinkedIn.module.scss'

const cn = AuthTemplate.cn

interface Props {
	correlationId?: string | null
	onClose: () => void
	open: boolean
	profile?: Profile
	headers?: LinkedInLoginHeaders | null
}

export function ModalConfirm({ correlationId, onClose, open, profile, headers }: Props) {
	const navigate = useNavigate()

	const [actionDone, setActionDone] = useState(false)
	const [loader, setLoader] = useState(false)

	const handlerModal = () => {
		if (!loader) {
			onClose()
		}
	}

	const handlerModalLink = async () => {
		if (!profile) {
			return
		}

		if (!headers?.cookies?.length) {
			notifyError(
				new Error(
					'LinkedIn session is missing. Sign in again, then click Link.'
				)
			)
			return
		}

		setLoader(true)
		setActionDone(false)
		try {
			const metadata = appConnector.utils.createMetadata()
			await confirmLinkedInProfile(
				{
					entityId: profile.entityId,
					publicIdentifier: profile.publicIdentifier,
					objectId: profile.objectId,
					firstName: profile.firstName,
					lastName: profile.lastName,
					fullName: profile.fullName,
					occupation: profile.occupation,
					picture: profile.picture,
					salesNavigatorEntityId: profile.salesNavigatorEntityId ?? null
				},
				headers,
				correlationId
			)

			await appConnector.auth.refreshAccessToken(metadata)

			await connectorAPI.getLinkedProfile()
			setActionDone(true)
		} catch (error) {
			handlerModal()
			notifyError(error)
		} finally {
			setLoader(false)
		}
	}

	return (
		<ControlledDialog
			className={css.modalInner}
			onRequestClose={() => {
				handlerModal()
				if (actionDone) {
					navigate('/contacts')
					document.location.reload()
				}
			}}
			open={open}
			rootClassName={`${css.dialog} app-scroll`}
		>
			<button
				aria-label='Close.'
				className={css.close}
				onClick={handlerModal}
				title='Close.'
				type='button'
			>
				<StackIcon icon='close' stroke='currentcolor' width={9} />
			</button>

			<div className={css.bodyModal}>
				<h2>Link this account?</h2>

				<p className={cn.text} style={{ color: 'red' }}>You cannot change it later!</p>

				{!isEmpty(profile) && (
					<>
						<div className='modal-create-linked__avatar'>
							{profile.picture ? (
								<img alt='pictureUrl' src={profile.picture} />
							) : (
								<div className='fake-img' />
							)}

							<div className='modal-create-linked__avatar-in' />
						</div>

						<h3>
							{profile.firstName} {profile.lastName}
						</h3>

						<a
							className={clsx(css.email, typo.mediumBody)}
							href={`https://www.linkedin.com/in/${profile.publicIdentifier}`}
							rel='noopener noreferrer'
							target='_blank'
						>
							{' '}
							{`https://www.linkedin.com/in/${profile.publicIdentifier}`}
						</a>

						<div className={css.actions}>
							<Button
								className={css.action}
								onClick={handlerModal}
								type='button'
								typography={typo.largeButton}
								variant='secondary'
							>
								Back
							</Button>

							<LoadingButton
								className={css.action}
								isLoading={loader}
								onClick={handlerModalLink}
								type='button'
								typography={typo.largeButton}
							>
								Link
							</LoadingButton>
						</div>
					</>
				)}
			</div>
		</ControlledDialog>
	)
}
